import { useMemo } from 'react'
import { adminImage, formatAdminMoney as formatMoney, formatFeatureWindow } from './adminFormatters'

function dateIsPast(value, now = Date.now()) {
  return value ? new Date(value).getTime() <= now : false
}

function adminOrderStage(order) {
  if (order.status === 'paid') {
    return { stage: 'Ship ready', tone: 'success', action: 'Ship ready' }
  }

  if (order.status === 'shipped') {
    return { stage: 'Shipped', tone: 'success', action: 'Monitor delivery' }
  }

  if (order.status === 'relist-queued') {
    return { stage: 'Relist queued', tone: 'danger', action: 'Relist queued' }
  }

  if (order.status === 'penalty-applied') {
    return dateIsPast(order.relistEligibleAt)
      ? { stage: 'Relist ready', tone: 'danger', action: 'Queue relist' }
      : { stage: 'Penalty applied', tone: 'danger', action: 'Collect payment' }
  }

  if (order.status === 'payment-pending') {
    return dateIsPast(order.penaltyAt)
      ? { stage: 'Penalty due', tone: 'danger', action: 'Apply penalty' }
      : { stage: 'Payment due', tone: 'warning', action: 'Reminder ready' }
  }

  if (order.status === 'weekly-hold') {
    return { stage: 'Weekly hold', tone: 'neutral', action: 'Friday reminder' }
  }

  return { stage: order.status?.replace(/-/g, ' ') || 'Monitor', tone: 'neutral', action: 'Monitor' }
}

function adminConsignmentStage(item) {
  if (item.workflowStage) {
    return { stage: item.workflowStage, tone: item.workflowTone || 'neutral' }
  }

  if (item.payoutStatus === 'paid') {
    return { stage: 'Payout complete', tone: 'success' }
  }

  if (item.saleStatus === 'sold') {
    return { stage: 'Sale sold', tone: 'warning' }
  }

  if (item.saleStatus === 'scheduled-auction' || item.saleStatus === 'buy-now-listing') {
    return { stage: 'Listed', tone: 'success' }
  }

  if (item.status === 'approved') {
    return { stage: 'Ready for decision', tone: 'warning' }
  }

  if (item.status === 'photos-needed') {
    return { stage: 'Photos needed', tone: 'danger' }
  }

  return { stage: item.status?.replace(/-/g, ' ') || 'Monitor', tone: 'neutral' }
}

export function buildAdminDashboardData(adminData) {
  const liveKpis = !adminData ? [] : [
    { label: 'GMV this week', value: formatMoney(adminData.metrics.gmv), note: 'Seeded marketplace volume', tone: 'success' },
    { label: 'Paid revenue', value: formatMoney(adminData.metrics.paidRevenue || 0), note: 'Successful checkout payments', tone: 'success' },
    { label: 'Open auctions', value: String(adminData.metrics.openAuctions), note: `${adminData.metrics.scheduledAuctions || 0} scheduled`, tone: 'success' },
    { label: 'Ready to ship', value: String(adminData.metrics.readyToShip || 0), note: `${adminData.metrics.penaltyOrders || 0} penalty orders`, tone: adminData.metrics.penaltyOrders ? 'danger' : 'neutral' },
  ]

  const liveAuctionRows = !adminData ? [] : adminData.auctions.map((auction) => ({
    rawId: auction.id,
    rawTitle: auction.title,
    rawSubtitle: auction.subtitle || '',
    rawDescription: auction.description || '',
    rawCategory: auction.category || 'Cards',
    rawType: auction.type,
    rawStatus: auction.status,
    rawFeatured: Boolean(auction.featured),
    rawStartsAt: auction.startsAt,
    rawEndsAt: auction.endsAt,
    rawWinnerId: auction.winnerId || '',
    rawHighBidderId: auction.highBidderId || '',
    rawPaymentStatus: auction.paymentStatus || '',
    rawFeaturedStartsAt: auction.featuredStartsAt,
    rawFeaturedEndsAt: auction.featuredEndsAt,
    rawBidCount: auction.bidCount || 0,
    rawCurrentBid: auction.currentBid || 0,
    rawNextBid: auction.nextBid || 0,
    rawMinimumIncrement: auction.minimumIncrement || 0,
    rawReservePrice: auction.reservePrice || 0,
    rawReserveMet: Boolean(auction.reserveMet || (auction.reservePrice <= auction.currentBid)),
    rawImages: auction.images || [],
    lot: auction.title,
    type: auction.type,
    status: auction.status,
    reserve: auction.reservePrice ? formatMoney(auction.reservePrice) : 'None',
    reserveState: auction.reservePrice ? (auction.reserveMet || auction.currentBid >= auction.reservePrice ? 'Met' : 'Open') : 'No reserve',
    currentBid: formatMoney(auction.currentBid || 0),
    nextBid: formatMoney(auction.nextBid || 0),
    increment: formatMoney(auction.minimumIncrement || 0),
    bids: String(auction.bidCount || 0),
    featured: auction.featured ? 'Featured' : 'Standard',
    featureWindow: formatFeatureWindow(auction.featuredStartsAt, auction.featuredEndsAt),
    close: new Date(auction.endsAt).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' }),
    image: adminImage(auction.images?.[0]),
  }))

  const liveOrderRows = !adminData ? [] : adminData.orders.map((order) => {
    const workflow = adminOrderStage(order)

    return {
      rawId: order.id,
      rawAuctionId: order.auctionId || '',
      rawListingId: order.listingId || '',
      rawType: order.type || '',
      rawPaymentDueAt: order.paymentDueAt,
      rawRelistEligibleAt: order.relistEligibleAt,
      rawPenaltyAt: order.penaltyAt,
      rawPenaltyApplied: Boolean(order.penaltyApplied),
      id: order.id.replace('ord_', 'OBC-'),
      user: order.userId.replace('user_', ''),
      status: order.status.replace(/-/g, ' '),
      workflowStage: workflow.stage,
      workflowTone: workflow.tone,
      total: formatMoney(order.total),
      shipping: order.shippingStatus?.replace(/-/g, ' ') || 'not ready',
      tracking: order.trackingNumber || 'Pending',
      reminders: String(order.reminderCount || 0),
      due: order.paymentDueAt ? new Date(order.paymentDueAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Friday hold',
      penaltyAt: order.penaltyAt ? new Date(order.penaltyAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Not scheduled',
      relistAt: order.relistEligibleAt ? new Date(order.relistEligibleAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Not eligible',
      action: workflow.action,
    }
  })

  const liveListingRows = !adminData?.listings ? [] : adminData.listings.map((listing) => ({
    rawId: listing.id,
    rawInventory: listing.inventory || 0,
    rawStatus: listing.status,
    rawBundleEligible: listing.bundleEligible,
    rawFeatured: Boolean(listing.featured),
    rawFeaturedStartsAt: listing.featuredStartsAt,
    rawFeaturedEndsAt: listing.featuredEndsAt,
    item: listing.title,
    category: listing.category,
    price: formatMoney(listing.price),
    inventory: `${listing.inventory || 0} left`,
    held: String(listing.heldCount || 0),
    sold: String(listing.soldCount || 0),
    status: listing.inventory <= 0 ? 'sold out' : listing.status,
    featured: listing.featured ? 'Featured' : 'Standard',
    featureWindow: formatFeatureWindow(listing.featuredStartsAt, listing.featuredEndsAt),
    image: adminImage(listing.images?.[0]),
  }))

  const liveUserRows = !adminData ? [] : adminData.users.map((user) => ({
    rawId: user.id,
    rawStatus: user.status,
    rawVerifiedBidder: user.verifiedBidder,
    rawRole: user.role,
    rawAdminPermissions: user.adminPermissions || [],
    user: user.name,
    role: user.role === 'admin' ? 'Admin' : user.verifiedBidder ? 'Verified bidder' : 'Collector',
    status: user.status,
    payment: user.savedPaymentMethods?.length ? 'Card saved' : 'No card',
    risk: user.verifiedBidder ? 'Good' : 'Verify bidder',
    permissions: user.role === 'admin'
      ? (user.adminPermissions?.length ? user.adminPermissions.join(', ') : 'Legacy full access')
      : 'Customer account',
  }))

  const liveConsignmentRows = !adminData ? [] : adminData.consignments.map((item) => {
    const stage = adminConsignmentStage(item)

    return {
      ...stage,
      id: item.id,
      item: item.itemTitle,
      consignor: item.consignorName,
      estimate: formatMoney(item.estimate || 0),
      saleAmount: formatMoney(item.saleAmount || 0),
      payoutAmount: formatMoney(item.payoutAmount || 0),
      expectedPayoutAmount: formatMoney(item.expectedPayoutAmount || 0),
      workflowStage: stage.stage,
      workflowTone: stage.tone,
      status: item.status?.replace(/-/g, ' ') || 'submitted',
      sale: item.saleStatus?.replace(/-/g, ' ') || 'pending review',
      payout: item.payoutStatus?.replace(/-/g, ' ') || 'not started',
      nextAction: item.nextAction || 'Monitor consignment',
      source: item.sourceLabel || 'Not listed',
      timeline: item.timeline || [],
      canConvert: Boolean(item.canConvert),
      canMarkPaid: Boolean(item.canMarkPaid),
      paidAt: item.payoutPaidAt ? new Date(item.payoutPaidAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Not paid',
      rawEstimate: item.estimate || 0,
      rawSaleAmount: item.saleAmount || 0,
      rawPayoutAmount: item.payoutAmount || 0,
      rawExpectedPayoutAmount: item.expectedPayoutAmount || 0,
      rawStatus: item.status,
      rawSaleStatus: item.saleStatus,
      rawPayoutStatus: item.payoutStatus,
      image: adminImage(item.images?.[0]),
    }
  })

  const liveBidRows = !adminData?.recentBids ? [] : adminData.recentBids.map((bid) => ({
    id: bid.id?.replace('bid_', 'BID-') || 'BID',
    bidder: bid.bidderName,
    masked: bid.bidderLabel,
    auction: bid.auctionTitle || bid.auctionId.replace('auc_', ''),
    status: bid.auctionStatus,
    amount: formatMoney(bid.amount),
    next: formatMoney(bid.nextBidAfter || 0),
    type: bid.auctionType,
    extended: bid.extendedClose ? 'Anti-snipe extended' : 'No extension',
    reserve: bid.reserveMetAfterBid ? 'Met' : 'Open',
    time: bid.createdAt ? new Date(bid.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Pending',
  }))

  const liveAuditRows = !adminData?.auditLogs ? [] : adminData.auditLogs.map((log) => ({
    actor: log.actorEmail || log.actorId,
    action: log.action.replace(/\./g, ' '),
    target: `${log.targetType}: ${log.targetId}`,
    summary: log.summary,
    time: new Date(log.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }),
  }))

  const reports = adminData?.metrics?.operationalReports
  const operationalReports = !reports ? [] : [
    {
      label: 'Payout exposure',
      value: formatMoney(reports.pendingPayoutAmount || 0),
      note: `${reports.pendingPayouts || 0} manual payouts pending`,
      tone: reports.pendingPayouts ? 'danger' : 'success',
      section: 'consignments',
    },
    {
      label: 'Unpaid risk',
      value: formatMoney(reports.unpaidOrderAmount || 0),
      note: `${reports.unpaidOrders || 0} orders need payment action`,
      tone: reports.unpaidOrders ? 'danger' : 'success',
      section: 'orders',
    },
    {
      label: 'Weekly holds',
      value: formatMoney(reports.weeklyHoldAmount || 0),
      note: `${reports.weeklyHoldOrders || 0} bundled orders waiting`,
      tone: reports.weeklyHoldOrders ? 'neutral' : 'success',
      section: 'orders',
    },
    {
      label: 'Homepage health',
      value: String((reports.activeFeaturedAuctions || 0) + (reports.activeFeaturedListings || 0)),
      note: `${reports.expiredFeaturedItems || 0} expired / ${reports.lowStockListings || 0} low-stock shop items`,
      tone: reports.expiredFeaturedItems || reports.lowStockListings ? 'danger' : 'success',
      section: 'content',
    },
  ]

  return {
    liveAuditRows,
    liveAuctionRows,
    liveBidRows,
    liveConsignmentRows,
    liveKpis,
    liveListingRows,
    liveOrderRows,
    liveUserRows,
    operationalReports,
  }
}

export function useAdminDashboardData(adminData) {
  return useMemo(() => buildAdminDashboardData(adminData), [adminData])
}
